import { useEffect, useRef, useState } from "react";
import * as PlyrJS from "plyr";
import "plyr/dist/plyr.css";
import { cn } from "@/lib/utils";
import { Play } from "lucide-react";
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

// Handle both ESM and CJS imports
const Plyr = (PlyrJS as any).default || PlyrJS;

interface PlyrYoutubeRendererProps {
  videoId: string;
  className?: string;
  isFullWidth?: boolean;
}

export default function PlyrYoutubeRenderer({ videoId, className, isFullWidth = false }: PlyrYoutubeRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailSrc, setThumbnailSrc] = useState("");

  // ⚠️ ANALYTICS: Video tracking state
  const hasTrackedPlay = useRef(false);
  const trackedMilestones = useRef<Set<number>>(new Set());
  const videoStartTime = useRef<number>(0);
  const videoTitle = useRef<string>('');

  // Border radius based on layout
  const radiusClass = isFullWidth ? "rounded-none" : "rounded-3xl";

  // Set thumbnail URL - try highest res first, fallback on error
  useEffect(() => {
    if (videoId) {
      setThumbnailSrc(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
    }
  }, [videoId]);

  // ⚠️ ANALYTICS: Attempt to get video title via oEmbed (CORS-friendly)
  useEffect(() => {
    if (videoId) {
      fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`)
        .then(res => res.json())
        .then(data => {
          if (data.title) {
            videoTitle.current = data.title;
          }
        })
        .catch(() => {
          // Silently fail - we'll use the fallback
        });
    }
  }, [videoId]);

  const handleThumbnailError = () => {
    // Fallback to hqdefault (always exists)
    if (videoId && thumbnailSrc.includes("maxresdefault")) {
      setThumbnailSrc(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);

    if (!hasTrackedPlay.current) {
      hasTrackedPlay.current = true;
      videoStartTime.current = Date.now();

      // ⚠️ ANALYTICS: Track video play start
      trackEvent(ANALYTICS_EVENTS.VIDEO_PLAYED, {
        video_id: videoId,
        video_provider: 'youtube',
        video_title: videoTitle.current || `YouTube Video ${videoId}`,
        article_url: window.location.pathname,
        is_full_width: isFullWidth,
      });
    }
  };

  useEffect(() => {
    if (!isPlaying || !containerRef.current || !videoId) return;

    // Initialize Plyr only after user clicks play
    playerRef.current = new Plyr(containerRef.current, {
      controls: ["play-large", "play", "progress", "current-time", "mute", "volume", "fullscreen"],
      youtube: {
        noCookie: true,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        playsinline: 1,
        autoplay: 1,
      },
      hideControls: true,
      clickToPlay: true,
      ratio: "16:9",
      fullscreen: { enabled: true, fallback: true, iosNative: true },
    });

    // Auto-play when ready
    playerRef.current.on("ready", () => {
      playerRef.current.play();
    });

    // ⚠️ ANALYTICS: Track video progress
    playerRef.current.on('timeupdate', () => {
      const player = playerRef.current;
      if (!player || !player.duration) return;

      const percentComplete = Math.round((player.currentTime / player.duration) * 100);

      [25, 50, 75, 100].forEach(milestone => {
        if (percentComplete >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone);

          trackEvent(ANALYTICS_EVENTS.VIDEO_PROGRESS, {
            video_id: videoId,
            video_provider: 'youtube',
            video_title: videoTitle.current || `YouTube Video ${videoId}`,
            milestone_percent: milestone,
            time_watched_ms: Date.now() - videoStartTime.current,
            article_url: window.location.pathname,
          });

          // Track completion at 100%
          if (milestone === 100) {
            trackEvent(ANALYTICS_EVENTS.VIDEO_COMPLETED, {
              video_id: videoId,
              video_provider: 'youtube',
              video_title: videoTitle.current || `YouTube Video ${videoId}`,
              total_watch_time_ms: Date.now() - videoStartTime.current,
              article_url: window.location.pathname,
            });
          }
        }
      });
    });

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isPlaying, videoId]);

  if (!videoId) {
    return (
      <div className={cn("aspect-video bg-muted rounded-lg flex items-center justify-center", className)}>
        <span className="text-muted-foreground text-sm">Video unavailable</span>
      </div>
    );
  }

  return (
    <figure className={cn("plyr-youtube my-6", className)}>
      {/* Custom poster with play button - shown before play */}
      {!isPlaying && thumbnailSrc && (
        <div 
          className={cn("relative aspect-video overflow-hidden cursor-pointer bg-black", radiusClass)}
          onClick={handlePlay}
        >
          <img
            src={thumbnailSrc}
            alt="Video thumbnail"
            loading="lazy"
            decoding="async"
            className="w-full h-[115%] object-cover object-center absolute top-1/2 left-0 -translate-y-1/2"
            onError={handleThumbnailError}
          />
          {/* Custom play button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-black/70 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
              <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
            </div>
          </div>
        </div>
      )}

      {/* Plyr container - shown after play */}
      {isPlaying && (
        <div className={cn("plyr-container overflow-hidden aspect-video", radiusClass)}>
          <div
            ref={containerRef}
            data-plyr-provider="youtube"
            data-plyr-embed-id={videoId}
            className={radiusClass}
          />
        </div>
      )}
    </figure>
  );
}
